import uniqid from 'uniqid';

// constants
import { BFR, NO_SUPERCHARGERS } from '../../../lib/Constants';

// helpers
import MapUtils from '../../../lib/MapUtils';
import MapMarkers from '../../../lib/MapMarkers';
import MapWindow from '../../../lib/MapWindow';
import AnalyticsUtils from '../../../lib/AnalyticsUtils';
import PlaceUtils from '../../../lib/PlaceUtils';
import AutocompleteUtils from '../../../lib/AutocompleteUtils';
import {
  makeActionCreator,
  getCountryCode,
  getShortAddressFromAddressComponents,
} from '../../../lib/Utils';

// actions
import {
  toggleDirectionsUI,
  bindDirectionsDraggable,
} from '../../Directions/actions';
// eslint-disable-next-line import/no-cycle
import { captureAppState } from '../../App/actions';
import {
  updateSuperchargersRoutechargers,
  updateSuperchargersCharging,
} from '../../Superchargers/actions';
import { setVehicleModelCode, setVehicleName } from '../../Vehicle/actions';

export const PLACES_SET_COORDS = 'PLACES_SET_COORDS';
export const setPlacesCoords = makeActionCreator(
  PLACES_SET_COORDS,
  'field',
  'coords',
);
export function updatePlacesCoords({ fields }, action) {
  // eslint-disable-next-line arrow-parens
  const newFields = fields.map(currentValue => {
    if (currentValue.id !== action.field.name) {
      return currentValue;
    }

    return Object.assign({}, currentValue, {
      lat: action.coords.lat,
      lng: action.coords.lng,
    });
  });

  return newFields;
}

export const PLACES_SET_ADDRESS = 'PLACES_SET_ADDRESS';
export const setPlacesAddress = makeActionCreator(
  PLACES_SET_ADDRESS,
  'field',
  'address',
  'shortAddress',
  'countryCode',
  'analytics',
);
export function updatePlacesAddress({ fields }, action) {
  // eslint-disable-next-line arrow-parens
  const newFields = fields.map(currentValue => {
    if (currentValue.id !== action.field.name) {
      return currentValue;
    }

    return Object.assign({}, currentValue, {
      address: action.address,
      shortAddress: action.shortAddress,
      country: action.countryCode,
      analytics: action.analytics,
    });
  });

  return newFields;
}

export const PLACES_REVERSE_ORDER = 'PLACES_REVERSE_ORDER';
export const reversePlacesOrder = makeActionCreator(
  PLACES_REVERSE_ORDER,
  'origin',
  'destination',
);
export function swapPlaces({ fields }) {
  const reversedFields = fields.slice().reverse();

  const newFields = reversedFields.map((field, index, array) => {
    let defaultText;
    if (index === 0) {
      // eslint-disable-next-line prefer-destructuring
      defaultText = reversedFields[array.length - 1].defaultText;
    } else {
      // eslint-disable-next-line prefer-destructuring
      defaultText = reversedFields[index - 1].defaultText;
    }

    return Object.assign({}, field, {
      defaultText,
      order: index,
    });
  });

  MapMarkers.updateTripMarkers(newFields);

  return newFields;
}

export const PLACES_VALIDATE_TRIP = 'PLACES_VALIDATE_TRIP';
export const validatePlacesTrip = makeActionCreator(PLACES_VALIDATE_TRIP);
export function updatePlacesValidity({ fields }) {
  const validPlaces = fields.filter(
    field => field.lat !== 0 && field.lng !== 0,
  );

  return validPlaces.length >= 2;
}

export const PLACES_CLEAR = 'PLACES_CLEAR';
export const clearPlaces = makeActionCreator(PLACES_CLEAR);
export function removePlaces({ fields }) {
  // eslint-disable-next-line arrow-parens
  let newFields = fields.map(currentValue => {
    MapMarkers.deleteMapMarker(currentValue, true);

    return Object.assign({}, currentValue, {
      lat: 0,
      lng: 0,
      address: '',
      shortAddress: '',
      country: '',
    });
  });

  newFields = newFields.slice(0, 2);

  MapUtils.deletePolyline();

  return newFields;
}

export const PLACES_TOGGLE_EDIT = 'PLACES_TOGGLE_EDIT';
export const toggleEditPlaces = makeActionCreator(
  PLACES_TOGGLE_EDIT,
  'editing',
);
export function editPlaces(currentState) {
  // eslint-disable-next-line arrow-parens
  return dispatch => {
    dispatch(toggleEditPlaces(true));
    dispatch(toggleDirectionsUI(false, false));
    dispatch(captureAppState(currentState));
    dispatch(bindDirectionsDraggable(false));
    MapWindow.closeInfoWindow(currentState.App.isMobile);
  };
}

export const PLACES_RESTORE = 'PLACES_RESTORE';
export const restorePlaces = makeActionCreator(PLACES_RESTORE, 'prevState');
export function hydratePlaces(state, action) {
  const fieldsLen = action.prevState.fields.length - 1;

  // clean up any new markers that were added while editing
  const { fields } = state;
  MapMarkers.deleteMapMarkers(fields, true);

  // eslint-disable-next-line arrow-parens
  const newFields = action.prevState.fields.map(currentValue => {
    if (action.prevState.tripIsValid) {
      // eslint-disable-next-line nonblock-statement-body-position
      MapMarkers.setMapMarker(currentValue, fieldsLen);
    }

    return Object.assign({}, currentValue, {
      id: currentValue.id,
      lat: currentValue.lat,
      lng: currentValue.lng,
      address: currentValue.address,
      shortAddress: currentValue.shortAddress,
      defaultText: currentValue.defaultText,
      order: currentValue.order,
      country: currentValue.country,
    });
  });

  return {
    fields: newFields,
    fieldsLength: newFields.length,
    tripIsValid: action.prevState.tripIsValid,
    numberOfStopoversSingular: action.prevState.numberOfStopoversSingular,
    numberOfStopoversPlural: action.prevState.numberOfStopoversPlural,
  };
}

export const PLACES_SET_FOCUSED = 'PLACES_SET_FOCUSED';
export const setFocusedPlaces = makeActionCreator(
  PLACES_SET_FOCUSED,
  'focused',
);

export function setPlace(field, place, fieldsLen) {
  // eslint-disable-next-line arrow-parens
  return dispatch => {
    const index = Number(field.id);
    PlaceUtils.focusNextField(index);

    const coords = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };
    dispatch(setPlacesCoords(field, coords));

    const placeDetails = {
      order: field.id,
      id: field.name,
      lat: coords.lat,
      lng: coords.lng,
    };
    MapMarkers.setMapMarker(placeDetails, fieldsLen);

    const shortAddress = getShortAddressFromAddressComponents(place);

    const analyticsData = AnalyticsUtils.getAnalyticsFromGeocoding(
      place.address_components,
    );
    const countryCode = getCountryCode(place.address_components);
    dispatch(
      setPlacesAddress(
        field,
        field.value,
        shortAddress,
        countryCode,
        analyticsData,
      ),
    );

    dispatch(validatePlacesTrip());
  };
}

export function erasePlace(field) {
  // eslint-disable-next-line arrow-parens
  return dispatch => {
    dispatch(setPlacesCoords(field, { lat: 0, lng: 0 }));
    const placeDetails = { order: field.id, id: field.name };
    MapMarkers.deleteMapMarker(placeDetails, true);
    MapUtils.deletePolyline();
    dispatch(validatePlacesTrip());
  };
}

export const PLACES_ADD_FIELD = 'PLACES_ADD_FIELD';
export const addPlacesField = makeActionCreator(PLACES_ADD_FIELD);
export function addField({ fields }) {
  const newFields = [
    ...fields,
    {
      id: uniqid(),
      lat: 0,
      lng: 0,
      address: '',
      shortAddress: '',
      defaultText: fields[1].defaultText,
      order: fields.length,
      country: fields[1].country,
    },
  ];

  MapMarkers.updateTripMarkers(newFields);

  return newFields;
}

export const PLACES_REMOVE_FIELD = 'PLACES_REMOVE_FIELD';
export const removePlacesField = makeActionCreator(
  PLACES_REMOVE_FIELD,
  'field',
);
export function removeField({ fields }, action) {
  MapMarkers.deleteMapMarker(action.field, true);

  let newFields = fields.filter(
    currentValue => currentValue.id !== action.field.id,
  );

  newFields = newFields.map(
    (currentValue, index) =>
      // eslint-disable-next-line implicit-arrow-linebreak
      Object.assign({}, currentValue, { order: index }),
    // eslint-disable-next-line function-paren-newline
  );

  MapMarkers.updateTripMarkers(newFields);

  return newFields;
}

export const PLACES_REORDER_FIELDS = 'PLACES_REORDER_FIELDS';
export const reorderPlacesFields = makeActionCreator(
  PLACES_REORDER_FIELDS,
  'field',
  'prevIndex',
  'nextIndex',
);
export function reorderFields({ fields }, action) {
  const oldFields = [...fields];
  const spliced = fields.splice(action.prevIndex, 1);
  fields.splice(action.nextIndex, 0, spliced[0]);

  const newFields = fields.map((currentValue, index) => {
    if (index === 0) {
      return Object.assign({}, currentValue, {
        defaultText: oldFields[index].defaultText,
        order: index,
      });
    }

    return Object.assign({}, currentValue, {
      defaultText: oldFields[index].defaultText,
      order: index,
    });
  });

  MapMarkers.updateTripMarkers(newFields);

  return newFields;
}

export const PLACES_UPDATE_LENGTH = 'PLACES_UPDATE_LENGTH';
export const updatePlacesLength = makeActionCreator(PLACES_UPDATE_LENGTH);

export function setPlanet({ place, planet }) {
  // eslint-disable-next-line arrow-parens
  return dispatch => {
    const coords = { lat: 999, lng: 999 };
    dispatch(setPlacesCoords(place, coords));

    dispatch(setPlacesAddress(place, place.value, planet, 'PM'));

    dispatch(validatePlacesTrip());

    AutocompleteUtils.clearPredictions();

    dispatch(setVehicleModelCode(BFR));
    dispatch(setVehicleName(BFR));
    dispatch(updateSuperchargersCharging(false));
    AnalyticsUtils.pushRouteError({ error: NO_SUPERCHARGERS });
  };
}

export function clearPlacesField(field) {
  return (dispatch, getState) => {
    const { superchargers } = getState().Superchargers;
    dispatch(removePlacesField(field));
    dispatch(erasePlace(field));
    dispatch(updateSuperchargersRoutechargers([]));
    MapMarkers.updateChargerIconSize(superchargers);
  };
}
